// # packages
import * as React from 'react';
import { boundMethod } from 'autobind-decorator';

export interface iProps {
    /**
     * Applies the "active" style to this tab.
     */
    active?: boolean;
    /**
     * Tab content.
     */
    children: React.ReactNode;
    /**
     * Whether this tab is disabled (cannot be selected)
     * Default is false.
     */
    disabled?: boolean;
    /**
     * A unique ID for this tab.
     * This allows this tab to be selected via the "activaTab" prop of <TabContent>.
     */
    id: number | string;
    /**
     * Called when the tab is clicked.
     */
    onClick?: (id: number | string) => any;
    /**
     * The visual theme of this tab.
     */
    theme?: 'danger'
}

export class Tab extends React.Component<iProps> {
    static displayName = 'Tab';

    /**
     * Renders the content.
     *
     * @returns {React.ReactNode}
     */
    public render(): React.ReactNode {
        const { active, children, disabled, theme } = this.props;

        const classNames = [
            'tabs__tab',
            active ? 'tabs__tab--active' : '',
            disabled ? 'tabs__tab--disabled': '',
            theme ? `tabs__tab--theme-${theme}` : '',
        ];

        return (
            <div
                aria-label={children.toString()}
                className={classNames.join(' ')}
                onClick={this.handleClick}
                role="button"
                title={children.toString()}
            >
                <div className="tabs__tab-content">
                    {children}
                </div>
            </div>
        );
    }

    /**
     * Handles clicks on the element.
     * Calls props.onClick if this tab is not disabled.
     *
     * @returns {void}
     */
    @boundMethod
    protected handleClick(): void {
        const { disabled, id, onClick } = this.props;

        if (disabled) { return; }

        if (onClick) {
            onClick(id);
        }
    }
}
